import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Leaf, Droplets, Heart, Shield, DollarSign, Award } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroSlides = [
  {
    image: `${process.env.PUBLIC_URL || ''}/images/engagement-rings-diamond_og.webp`,
    title: 'Preferred Partner of Wholesalers, Jewellers and Retailers',
    subtitle: 'for Lab-Grown Diamonds'
  },
  {
    image: `${process.env.PUBLIC_URL || ''}/images/necklace.png`,
    title: 'Preferred Partner of Wholesalers, Jewellers and Retailers',
    subtitle: 'for Lab-Grown Diamonds'
  },
  {
    image: `${process.env.PUBLIC_URL || ''}/images/necklace2.png`,
    title: 'Preferred Partner of Wholesalers, Jewellers and Retailers',
    subtitle: 'for Lab-Grown Diamonds'
  },
  {
    image: `${process.env.PUBLIC_URL || ''}/images/marquise.png`,
    title: 'Preferred Partner of Wholesalers, Jewellers and Retailers',
    subtitle: 'for Lab-Grown Diamonds'
  },
  {
    image: `${process.env.PUBLIC_URL || ''}/images/heart.png`,
    title: 'Preferred Partner of Wholesalers, Jewellers and Retailers',
    subtitle: 'for Lab-Grown Diamonds'
  },
  {
    image: `${process.env.PUBLIC_URL || ''}/images/emerald.png`,
    title: 'Preferred Partner of Wholesalers, Jewellers and Retailers',
    subtitle: 'for Lab-Grown Diamonds'
  }];


  const benefits = [
  {
    icon: <Leaf size={40} />,
    title: 'Earth Friendly',
    description: '1 carat saves 200-250 tonnes of land extraction and protects natural ecosystems',
    image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09'
  },
  {
    icon: <Droplets size={40} />,
    title: 'Water Conservation',
    description: 'Each carat saves 109 gallons of precious water resources',
    image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7'
  },
  {
    icon: <Shield size={40} />,
    title: 'Conflict Free',
    description: 'The only TRUE conflict-free diamonds, protecting human rights',
    image: 'https://images.pexels.com/photos/957024/forest-trees-perspective-bright-957024.jpeg'
  },
  {
    icon: <Heart size={40} />,
    title: '100% Real',
    description: 'Identical chemical, physical, and optical properties to mined diamonds'
  },
  {
    icon: <Award size={40} />,
    title: 'Superior Quality',
    description: 'Created with advanced technology and 50+ years of expertise'
  },
  {
    icon: <DollarSign size={40} />,
    title: 'Best Value',
    description: 'Premium quality at a fraction of the cost of mined diamonds'
  }];


  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Navbar />

      {/* Hero Carousel */}
      <section className="relative h-[600px] md:h-[700px] overflow-hidden">
        {heroSlides.map((slide, index) =>
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
          index === currentSlide ? 'opacity-100' : 'opacity-0'}`
          }>

            <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover"
            loading="lazy" />

            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
              <div className="text-center text-white px-6">
                <h1 className="hero-large mb-4" style={{ color: 'white' }}>{slide.title}</h1>
                <p className="hero-medium opacity-90" style={{ color: 'white' }}>{slide.subtitle}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <button
          onClick={prevSlide}
          className="absolute left-6 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-40 p-3 transition-all">

          <ChevronLeft size={32} className="text-white" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-6 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-40 p-3 transition-all">

          <ChevronRight size={32} className="text-white" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
          {heroSlides.map((_, index) =>
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 border-2 border-white transition-all ${
            index === currentSlide ? 'bg-white' : 'bg-transparent'}`
            } />

          )}
        </div>
      </section>

      {/* Introduction Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="hero-medium mb-6">Your Preferred Partner for Lab-Grown Diamonds</h2>
            <p className="body-large mb-8">
              Welcome to <span className="font-semibold text-[var(--accent-green-dark)]">Magnificent Diamond</span>,
              a leading and trusted manufacturer and supplier of lab-grown diamonds.
            </p>
            <div className="flex flex-wrap justify-center gap-8 mb-8">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-3 bg-[var(--accent-green-pale)] flex items-center justify-center transition-all hover:bg-[var(--accent-green-medium)]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green-dark)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                </div>
                <h3 className="heading-3 mb-1">Customer First</h3>
                <p className="body-small">Approach</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-3 bg-[var(--accent-green-pale)] flex items-center justify-center transition-all hover:bg-[var(--accent-green-medium)]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green-dark)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>
                </div>
                <h3 className="heading-3 mb-1">Consistent</h3>
                <p className="body-small">Assortment</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-3 bg-[var(--accent-green-pale)] flex items-center justify-center transition-all hover:bg-[var(--accent-green-medium)]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green-dark)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
                </div>
                <h3 className="heading-3 mb-1">Stable</h3>
                <p className="body-small">Supply</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-3 bg-[var(--accent-green-pale)] flex items-center justify-center transition-all hover:bg-[var(--accent-green-medium)]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green-dark)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
                </div>
                <h3 className="heading-3 mb-1">Best Value</h3>
                <p className="body-small">Total Cost</p>
              </div>
            </div>
            <div className="inline-block bg-[var(--accent-green-dark)] text-white px-8 py-4">
              <p className="heading-2 !text-[#FFFFFF]" style={{ letterSpacing: '2px' }}>REAL • PURE • BETTER</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding-small bg-[var(--bg-secondary)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="hero-medium mb-4">Why Choose Lab-Grown Diamonds</h2>
            <p className="body-large">Luxury that embraces science and sustainability</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) =>
            <div
              key={index}
              className="bg-white p-8 hover-lift cursor-pointer">

                {benefit.image &&
              <div className="mb-6 h-48 overflow-hidden">
                    <img
                  src={benefit.image}
                  alt={benefit.title}
                  className="w-full h-full object-cover"
                  loading="lazy" />

                  </div>
              }
                <div className="text-[var(--accent-green-medium)] mb-4">{benefit.icon}</div>
                <h3 className="heading-3 mb-3">{benefit.title}</h3>
                <p className="body-regular">{benefit.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-[var(--accent-green-dark)] text-white p-16 text-center">
            <h2 className="hero-medium mb-6 !text-[#FFFFFF]">
              Partner with Magnificent Diamond
            </h2>
            <p className="body-large max-w-2xl mb-8 mx-auto !text-[#FFFFFF]">
              Join leading retailers and jewellers across the USA who trust us as their preferred partner
            </p>
            <Link to="/contact" className="btn-primary" style={{ borderColor: 'white', color: 'white' }}>
              <span className="button-text">Get in Touch</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>);

};

export default HomePage;